// src/contexts/AuthContext.js
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import axios from 'axios';
import {
	TIMEOUTS,
	authRefreshClientVerboseLogging,
	getSessionIdleConfigDevLogPayload,
} from '../config/timeouts';
import { buildApiUrl } from '../utils/apiUrl';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

/** Tolker auth-respons: server sender gjerne `sessionIdleRemainingSeconds` for korrekt nedtelling. */
const readSessionIdleSyncFromResponse = (data) => {
	const iso = data?.sessionIdleExpiresAt || null;
	let rem =
		typeof data?.sessionIdleRemainingSeconds === 'number' &&
		Number.isFinite(data.sessionIdleRemainingSeconds)
			? Math.max(0, Math.floor(data.sessionIdleRemainingSeconds))
			: null;
	if (iso && rem === null) {
		const expMs = new Date(iso).getTime();
		if (Number.isFinite(expMs)) {
			rem = Math.max(0, Math.floor((expMs - Date.now()) / 1000));
		}
	}
	const syncedAtClientMs = iso || rem !== null ? Date.now() : null;
	return { iso, rem, syncedAtClientMs };
};

export const AuthProvider = ({ children }) => {
	const [devMailMode, setDevMailMode] = useState(() => {
		try {
			return localStorage.getItem('devMailMode') === 'true';
		} catch (error) {
			return false;
		}
	});
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [userRole, setUserRole] = useState('');
	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [accessTokenExpiresAt, setAccessTokenExpiresAt] = useState(null);
	const [sessionIdleExpiresAt, setSessionIdleExpiresAt] = useState(null);
	const [sessionIdleRemainingSeconds, setSessionIdleRemainingSeconds] =
		useState(null);
	const [sessionIdleSyncedAtClientMs, setSessionIdleSyncedAtClientMs] =
		useState(null);

	const hasLoggedSessionIdleConfigRef = useRef(false);

	const logSessionIdleConfigDevOnce = useCallback(() => {
		if (
			process.env.NODE_ENV !== 'development' ||
			hasLoggedSessionIdleConfigRef.current
		) {
			return;
		}
		hasLoggedSessionIdleConfigRef.current = true;
		console.info(
			'[auth/config] Timeout-fasit (`frontend/src/config/timeouts.js`):',
			getSessionIdleConfigDevLogPayload()
		);
	}, []);

	useEffect(() => {
		try {
			localStorage.setItem('devMailMode', String(devMailMode));
		} catch (error) {
			// Ignore storage errors (private mode, quota, etc.)
		}
	}, [devMailMode]);

	const checkAuthStatus = useCallback(async () => {
		try {
			const response = await axios.get(buildApiUrl('auth/check'), {
				withCredentials: true,
			});

			if (response.data.isAuthenticated) {
				setIsLoggedIn(true);
				setUserRole(response.data.user.role);
				setUser(response.data.user);
				setAccessTokenExpiresAt(
					response.data.accessTokenExpiresAt || null
				);
				const idle = readSessionIdleSyncFromResponse(response.data);
				setSessionIdleExpiresAt(idle.iso);
				setSessionIdleRemainingSeconds(idle.rem);
				setSessionIdleSyncedAtClientMs(idle.syncedAtClientMs);
				logSessionIdleConfigDevOnce();
				try {
					localStorage.removeItem('authLogoutReason');
				} catch {
					/* ignore */
				}
			} else {
				setIsLoggedIn(false);
				setUserRole('');
				setUser(null);
				setAccessTokenExpiresAt(null);
				setSessionIdleExpiresAt(null);
				setSessionIdleRemainingSeconds(null);
				setSessionIdleSyncedAtClientMs(null);
			}
		} catch (error) {
			setIsLoggedIn(false);
			setUserRole('');
			setUser(null);
			setAccessTokenExpiresAt(null);
			setSessionIdleExpiresAt(null);
			setSessionIdleRemainingSeconds(null);
			setSessionIdleSyncedAtClientMs(null);
		} finally {
			setIsLoading(false);
		}
	}, [logSessionIdleConfigDevOnce]);

	// Check authentication status on mount
	useEffect(() => {
		checkAuthStatus();
	}, [checkAuthStatus]);

	const login = useCallback(async (loginData) => {
		try {
			const response = await axios.post(buildApiUrl('auth/login'), loginData, {
				withCredentials: true,
			});

			setIsLoggedIn(true);
			setUserRole(response.data.user.role);
			setUser(response.data.user);
			setAccessTokenExpiresAt(
				response.data.accessTokenExpiresAt || null
			);
			const idle = readSessionIdleSyncFromResponse(response.data);
			setSessionIdleExpiresAt(idle.iso);
			setSessionIdleRemainingSeconds(idle.rem);
			setSessionIdleSyncedAtClientMs(idle.syncedAtClientMs);
			logSessionIdleConfigDevOnce();
			try {
				localStorage.removeItem('authLogoutReason');
			} catch {
				/* ignore */
			}

			return { success: true, user: response.data.user };
		} catch (error) {
			const res = error.response;
			const data = res?.data;
			return {
				success: false,
				error: data?.message || 'Login failed',
				code: data?.code,
				httpStatus: res?.status,
			};
		}
	}, [logSessionIdleConfigDevOnce]);

	const logout = useCallback(async () => {
		try {
			await axios.post(buildApiUrl('auth/logout'), {}, { withCredentials: true });
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			setIsLoggedIn(false);
			setUserRole('');
			setUser(null);
			setAccessTokenExpiresAt(null);
			setSessionIdleExpiresAt(null);
			setSessionIdleRemainingSeconds(null);
			setSessionIdleSyncedAtClientMs(null);
			hasLoggedSessionIdleConfigRef.current = false;
			try {
				localStorage.removeItem('authLogoutReason');
			} catch {
				/* ignore */
			}
		}
	}, []);

	const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	/** P1-6: flere forsøk med backoff ved nettfeil før vi gir opp. */
	const refreshToken = useCallback(async () => {
		const maxAttempts = TIMEOUTS.authRefreshMaxAttempts;
		let lastError;
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			try {
				const response = await axios.post(
					buildApiUrl('auth/refresh'),
					{},
					{ withCredentials: true }
				);
				if (response.data?.accessTokenExpiresAt) {
					setAccessTokenExpiresAt(response.data.accessTokenExpiresAt);
				}
				if (
					response.data?.sessionIdleExpiresAt ||
						typeof response.data?.sessionIdleRemainingSeconds ===
							'number'
				) {
					const idle = readSessionIdleSyncFromResponse(response.data);
					setSessionIdleExpiresAt(idle.iso);
					setSessionIdleRemainingSeconds(idle.rem);
					setSessionIdleSyncedAtClientMs(idle.syncedAtClientMs);
				}
				if (authRefreshClientVerboseLogging) {
					console.log('[auth/refresh:client]', {
						ts: new Date().toISOString(),
						attempt: attempt + 1,
						sessionIdleRemainingSeconds:
							response.data?.sessionIdleRemainingSeconds,
						sessionIdleExpiresAt: response.data?.sessionIdleExpiresAt,
					});
				}
				return true;
			} catch (error) {
				lastError = error;
				const status = error.response?.status;
				if (status === 401 || status === 403) {
					const lr = error.response?.data?.logoutReason;
					if (lr) {
						try {
							localStorage.setItem('authLogoutReason', lr);
						} catch {
							/* ignore */
						}
					}
					break;
				}
				if (attempt < maxAttempts - 1) {
					await sleep(TIMEOUTS.authRefreshBackoffBaseMs * 2 ** attempt);
				}
			}
		}
		if (lastError) {
			console.error('Token refresh failed:', lastError);
		}
		return false;
	}, []);

	return (
		<AuthContext.Provider
			value={{
				isLoggedIn,
				setIsLoggedIn,
				userRole,
				setUserRole,
				user,
				setUser,
				devMailMode,
				setDevMailMode,
				isLoading,
				accessTokenExpiresAt,
				setAccessTokenExpiresAt,
				sessionIdleExpiresAt,
				setSessionIdleExpiresAt,
				sessionIdleRemainingSeconds,
				sessionIdleSyncedAtClientMs,
				login,
				logout,
				refreshToken,
				checkAuthStatus,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
